import {useCallback} from 'react';
import sdk from '@/sdk';
import {useIntl} from 'react-intl';
import {ApplyRole, MemberType, ObjectType, Operation, Role} from '@/Constant';
import assert from 'assert';
import {DocMembers, Member} from '@/interface';
import toast, {ToastType} from '@/components/Toast';
import {ErrorCode} from '@/sdk/common/error';
import {feLogger, pageSendApply, permissionApply} from '@/buried';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';

// 选择管理员
export enum SELECT_ADMIN_TYPE {
    unAdmin = 1, // 未选择管理员
    docAdmin = 2, // 选择页面管理员
    repoAdmin = 3, // 选择知识库管理员
    creator = 4, // 页面创建者
    docEditor = 5, // 最近编辑者
}

export function selectAdmin(roleName:string|undefined) {
    if (roleName === Role.Admin) {
        return SELECT_ADMIN_TYPE.repoAdmin;
    } else if (roleName === Role.DocAdmin) {
        return SELECT_ADMIN_TYPE.docAdmin;
    } else if (roleName === Role.Owner) {
        return SELECT_ADMIN_TYPE.creator;
    } else if (roleName === ApplyRole.DocEditor) {
        return SELECT_ADMIN_TYPE.docEditor;
    }
    return SELECT_ADMIN_TYPE.unAdmin;
}

// 申请者当前的权限
export enum CUR_USER_POWER {
    noAuth = 1,
    read = 2,
    edit = 3,
}

export function getRoleType(role: string | undefined) {
    const readMap = [Role.Reader, Role.DocReader];
    const editMap = [Role.Member, Role.DocMember];
    const repoNoAuth = [Role.LimitedReader];
    if (!role || repoNoAuth.includes(role)) {
        return CUR_USER_POWER.noAuth;
    }
    if (readMap.includes(role)) {
        return CUR_USER_POWER.read;
    }
    if (editMap.includes(role)) {
        return CUR_USER_POWER.edit;
    }
    return role; // 理论上代码不会走到这里，上面的会覆盖全，但是避免有不知道权限，上报上去好定位
}

// 要申请的权限-页面
export enum APPLY_PERMISSION_PAGE {
    DocAdmin = 1, // 申请管理员
    DocMember = 2, // 申请编辑者
    DocReader = 3, // 申请查看者
}
// 要申请的权限-知识库
export enum APPLY_PERMISSION_REPO{
    Admin = 1, // 申请管理员
    Member = 2, // 申请编辑者
    Reader = 3, // 申请查看者
}

export function applyPermissionPage(role:string|undefined) {
    if (role === Role.DocAdmin) {
        return APPLY_PERMISSION_PAGE.DocAdmin;
    } else if (role === Role.DocMember) {
        return APPLY_PERMISSION_PAGE.DocMember;
    } else if (role === Role.DocReader) {
        return APPLY_PERMISSION_PAGE.DocReader;
    }
}

export function applyPermissionRepo(role:string|undefined) {
    if (role === Role.Admin) {
        return APPLY_PERMISSION_REPO.Admin;
    } else if (role === Role.Member) {
        return APPLY_PERMISSION_REPO.Member;
    } else if (role === Role.Reader) {
        return APPLY_PERMISSION_REPO.Reader;
    }
}
// 申请内容
export enum APP_CONT {
    Doc = 1, // 申请页面权限
    Repo = 2, // 申请知识库权限
}

// 文档/知识库
export enum APP_TYPE {
    Document = 1, // 文档
    Repository = 2, // 知识库
}

// 申请渠道
export enum APP_CHANNEL {
    docNoPermission = 1, // 在无权页面内进行申请
    repoRelated = 2, // 在知识库相关页面内进行申请
    repoMain = 3, // 在知识库的主页申请权限:
    readToApply = 4, // 有查看权限时，在页面进一步申请权限
    editToApply = 5, // 有编辑权限，在页面“权限”浮窗内进一步申请权限
    repoNoPermission = 6, // 知识库内无权连接权限申请:
}
export const useSendMessagesCard = (forceOldCard: boolean) => {
    return useCallback(
        (
            result: {
                memberEvents: Array<{
                    memberGuid: string;
                    eventGuid: string;
                }>;
            },
            selectAdmins: {
                [ObjectType.Doc]: any[];
                [ObjectType.Repository]: any[];
                [ObjectType.Book]: any[];
            },
            selectValue: {
                address: ObjectType.Doc | ObjectType.Repository | ObjectType.Book;
                role: string;
                message: string;
            },
            docInfo: {
                spaceGuid: string;
                groupGuid: string;
                repositoryGuid: string;
                docGuid?: string;
            }
        ) => {
            const relEvents = result && result.memberEvents;
            if (!relEvents) {
                return;
            }
            if (selectAdmins[selectValue.address].length === 0) {
                return;
            }
            const {spaceGuid, groupGuid, repositoryGuid, docGuid = ''} = docInfo;
            const data = {
                roleName: selectValue.role,
                message: selectValue.message,
                spaceGuid,
                groupGuid,
                repositoryGuid,
                docGuid: selectValue.address === ObjectType.Doc || selectValue.address === ObjectType.Book
                    ? docGuid || ''
                    : '',
            };
            const memberEvents = selectAdmins[selectValue.address].map(item => ({
                memberGuid: relEvents[0].memberGuid,
                eventGuid: relEvents[0].eventGuid,
                receiverGuid: item,
            }));
            if (forceOldCard) {
                sdk.as.sendAdminMessages({
                    ...data,
                    memberEvents,
                }).catch(err => {
                    console.log(err);
                });

            } else {
                sdk.as.sendAdminMessagesSingle({
                    ...data,
                    memberEvent: memberEvents[0],
                }).catch(err => {
                    console.log(err);
                });
            }
        }, [forceOldCard]);
};

export const useSendApplyForRepository = () => {
    const intl = useIntl();
    const sendMessageCard = useSendMessagesCard(true);
    return useCallback((
        selectAdmins: {
            [ObjectType.Doc]: any[];
            [ObjectType.Repository]: any[];
            [ObjectType.Book]: any[];
            roleName?: any;
        },
        selectValue: {
            address: ObjectType.Doc | ObjectType.Repository | ObjectType.Book;
            role: string;
            message: string;
        },
        docInfo: {
            spaceGuid: string;
            groupGuid: string;
            repositoryGuid: string;
            docGuid?: string;
        },
        callback?: ((params?: any) => void) | null
    ) => {
        assert(sdk.as.user);
        if (!sdk.as.user || !sdk.as.user.nickname) {
            return;
        }
        assert(selectValue.role);
        const member: Member = {
            memberGuid: sdk.as.user.userGuid,
            memberType: MemberType.User, // user
            action: Operation.Apply,
            alias: '',
            roleName: selectValue.role,
            message: selectValue.message,
            nickname: sdk.as.user.nickname,
        };
        const {spaceGuid, groupGuid, repositoryGuid} = docInfo;
        const selectedArr = selectAdmins[selectValue.address] || [];
        sdk.as.addMember(spaceGuid, groupGuid, repositoryGuid, member, true, selectedArr[0]).then(result => {
            feLogger(pageSendApply({
                target: selectAdmins[selectValue.address].length || 0,
                type: 'repo',
                permission: selectValue.role,
                remark: selectValue.message ? 1 : 0,
                eventGuid: result?.memberEvents[0]?.eventGuid || '',
            }));
            const globalBuriedParams = {
                app_cont: APP_CONT.Repo,
                app_channel: APP_CHANNEL.docNoPermission,
                app_power: applyPermissionRepo(selectValue.role),
                slelct_admin: selectAdmin(selectedArr.length ? selectAdmins?.roleName[selectValue.address] : ''),
                time: +new Date(),
                user_id: member.memberGuid,
                app_type: APP_TYPE.Repository,
                doc_id: repositoryGuid,
                admin_id: selectedArr[0],
                event_id: result?.memberEvents[0]?.eventGuid || '',
                tips: member.message,
            };
            callback && callback(globalBuriedParams);
            return sendMessageCard(
                result,
                selectAdmins,
                selectValue,
                {spaceGuid, groupGuid, repositoryGuid}
            );
        }).catch(error => {
            console.error(error);
            if (error.code === ErrorCode.Duplicate) {
                toast({
                    type: ToastType.Error,
                    manualClose: true,
                    content: intl.formatMessage({id: 'toastHasApplied'}),
                    duration: 3000,
                    onClose: () => {
                        window.location.reload();
                    },
                });
            }
            else if (error.code === ErrorCode.AdministratorLimit) {
                toast({content: error.message, type: ToastType.Error});
            }
            else {
                toast({
                    content: intl.formatMessage({id: 'toastApplyFailed'}),
                    type: ToastType.Error,
                });
            }

        });
    }, [intl, sendMessageCard]);
};

export const useSendApplyForDoc = (forceOldCard: boolean = false) => {
    const intl = useIntl();
    const sendMessageCard = useSendMessagesCard(forceOldCard);
    return useCallback(async (
        selectAdmins: {
            [ObjectType.Doc]: any[];
            [ObjectType.Repository]: any[];
            [ObjectType.Book]: any[];
            roleName?: any;
        },
        selectValue: {
            address: ObjectType.Doc | ObjectType.Repository | ObjectType.Book;
            role: string;
            message: string;
        },
        docInfo: {
            spaceGuid: string;
            groupGuid: string;
            repositoryGuid: string;
            docGuid?: string;
        },
        repository: Repository,
        callback?: ((params?: any) => void) | null,
        action?: string
    ) => {
        assert(sdk.as.user);
        if (!sdk.as.user || !sdk.as.user.nickname) {
            return;
        }
        assert(selectValue.role);
        const member: Member = {
            memberGuid: sdk.as.user.userGuid,
            memberType: MemberType.User,
            nickname: sdk.as.user.nickname,
        };
        const docMembers: DocMembers = {
            members: [member],
            action: action || Operation.Apply,
            roleName: selectValue.role,
            message: selectValue.message,
        };
        const {spaceGuid, groupGuid, repositoryGuid, docGuid = ''} = docInfo;
        const selectedArr = selectAdmins[selectValue.address] || [];
        if (docGuid) {
            const ksServer = await sdk.getKsServer(repository);
            ksServer.addDocMember(docGuid, {
                ...docMembers,
                receiverGuid: selectedArr[0],
            }, true).then(result => {
                feLogger(pageSendApply({
                    target: selectAdmins[selectValue.address].length || 0,
                    type: 'page',
                    permission: selectValue.role,
                    remark: selectValue.message ? 1 : 0,
                    eventGuid: result?.memberEvents[0]?.eventGuid || '',
                }));
                const globalParams = {
                    app_cont: APP_CONT.Doc,
                    app_channel: APP_CHANNEL.docNoPermission,
                    app_power: applyPermissionPage(selectValue.role),
                    slelct_admin: selectAdmin(selectedArr.length ? selectAdmins?.roleName[selectValue.address] : ''),
                    time: +new Date(),
                    user_id: member.memberGuid,
                    app_type: APP_TYPE.Document,
                    doc_id: docInfo.docGuid,
                    admin_id: selectedArr[0],
                    event_id: result?.memberEvents[0]?.eventGuid || '',
                    tips: docMembers.message,
                };
                callback && callback(globalParams);
                return sendMessageCard(
                    result,
                    selectAdmins,
                    selectValue,
                    {spaceGuid, groupGuid, repositoryGuid, docGuid}
                );
            }).catch(error => {
                console.error(error);
                if (error.code === ErrorCode.applyMemberError) {
                    toast({
                        type: ToastType.Error,
                        manualClose: true,
                        content: intl.formatMessage({id: 'toastHasApplyMemberUpdate'}),
                        duration: 3000,
                        onClose: () => {
                            window.location.reload();
                        },
                    });
                } else if (error.code === ErrorCode.Duplicate) {
                    toast({
                        type: ToastType.Error,
                        manualClose: true,
                        content: intl.formatMessage({id: 'toastHasApplied'}),
                        duration: 3000,
                        onClose: () => {
                            window.location.reload();
                        },
                    });
                } else {
                    toast({
                        content: intl.formatMessage({id: 'toastApplyFailed'}),
                        type: ToastType.Error,
                    });
                }
            });
        }
    }, [intl, sendMessageCard]);
};
